import axios from "axios";

import { clearUserData } from "./clearUserData";
import { ownerScopeQueryString, OWNER_SCOPE_STORAGE_KEY } from "./ownerScope";

export const baseURL = process.env.REACT_APP_API_URL;

// Access token lifetime in production is 15 minutes
// Refresh proactively at 80% of lifetime (12 minutes) to prevent expiration during use
const ACCESS_TOKEN_LIFETIME_MS = 15 * 60 * 1000; // 15 minutes
const PROACTIVE_REFRESH_AT_MS = ACCESS_TOKEN_LIFETIME_MS * 0.8; // 12 minutes

const OWNER_SCOPE_ENDPOINTS = [
  "/metrics/team-revenue/",
  "/metrics/dashboard-summary/",
  "/metrics/renewal-forecast-summary/",
  "/accounts/",
  "/metrics/churn/summary/",
  "/metrics/churn/accounts/",
  "/metrics/nrr-goal-progress/",
  "/priority-dashboard/",
  "/priority-dashboard/summary/",
];

const shouldAddOwnerScope = (url) => {
  const raw = url.split("?")[0];
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  return OWNER_SCOPE_ENDPOINTS.some(
    (endpoint) => path === endpoint || path === endpoint.slice(0, -1),
  );
};

const addOwnerScopeToUrl = (url) => {
  const token = localStorage.getItem(OWNER_SCOPE_STORAGE_KEY);
  const qs = ownerScopeQueryString(token);
  if (!qs) {
    return url;
  }
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}${qs}`;
};

export const axiosInstance = axios.create({
  baseURL,
  timeout: 20000, // 20 second timeout
  withCredentials: true, // Enable HttpOnly cookie-based auth
});

// Track retry attempts to prevent infinite loops
let isRefreshing = false;
let failedQueue = [];
let consecutiveFailures = 0;
const MAX_CONSECUTIVE_FAILURES = 3;
const BACKOFF_DELAY = 5000; // 5 seconds

// Track if we've already handled a logout to prevent multiple redirects
let isLoggingOut = false;

// Proactive refresh timer
let refreshTimer = null;

const processQueue = (error) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

const isNetworkError = (error) => {
  return (
    !error.response &&
    (error.code === "ENOTFOUND" ||
      error.code === "ECONNREFUSED" ||
      error.code === "ETIMEDOUT" ||
      error.message === "Network Error")
  );
};

/**
 * Check if an error indicates the session is invalid and refresh should be attempted.
 * - 401: Token expired or invalid
 * - 403: Can occur when access token cookie is missing (deleted after expiration)
 */
const isAuthError = (status) => {
  return status === 401 || status === 403;
};

const handleLogout = async () => {
  if (isLoggingOut) return; // Prevent multiple logout attempts

  isLoggingOut = true;
  console.log("Forcing logout due to authentication failure");

  // Clear proactive refresh timer
  stopProactiveRefresh();

  // Clear all user-specific localStorage
  clearUserData();

  // Call logout endpoint to clear HttpOnly cookies
  try {
    await axios.post(`${baseURL}/logout/`, {}, { withCredentials: true });
  } catch (error) {
    // Ignore errors - we're logging out anyway
    console.log("Logout endpoint call failed (non-critical):", error.message);
  }

  // Reset flags
  isRefreshing = false;
  consecutiveFailures = 0;

  // Redirect to login — but only if we're not already there (prevents reload loop)
  if (window.location.pathname !== "/login") {
    setTimeout(() => {
      window.location.href = "/login";
    }, 100);
  }
};

const refreshToken = async () => {
  try {
    // Cookie-based refresh - cookies sent automatically with withCredentials
    await axios.post(
      `${baseURL}/token/refresh/`,
      {}, // Empty body - cookies contain the refresh token
      {
        withCredentials: true,
        timeout: 10000,
      },
    );

    consecutiveFailures = 0; // Reset failure count on success

    // Schedule next proactive refresh
    scheduleProactiveRefresh();

    return true;
  } catch (error) {
    console.error("Error refreshing token:", error);

    // If it's a network error, don't logout immediately
    if (isNetworkError(error)) {
      consecutiveFailures++;
      if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        console.error("Max consecutive failures reached, logging out");
        handleLogout();
      }
      throw error;
    }

    // For non-network errors (401, etc), logout
    handleLogout();
    throw error;
  }
};

/**
 * Schedule a proactive token refresh before the access token expires.
 * This prevents 401/403 errors during active use.
 */
const scheduleProactiveRefresh = () => {
  // Clear any existing timer
  stopProactiveRefresh();

  // Schedule refresh at 80% of token lifetime
  refreshTimer = setTimeout(async () => {
    // Only refresh if we're not already refreshing and not logging out
    if (!isRefreshing && !isLoggingOut) {
      console.log("Proactive token refresh triggered");
      isRefreshing = true;
      try {
        await refreshToken();
        processQueue(null);
      } catch (error) {
        // Proactive refresh failed - user will get 401 on next request
        // which will trigger reactive refresh or logout
        console.error("Proactive refresh failed:", error);
      } finally {
        isRefreshing = false;
      }
    }
  }, PROACTIVE_REFRESH_AT_MS);
};

/**
 * Stop the proactive refresh timer.
 */
const stopProactiveRefresh = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

/**
 * Called after successful login to start proactive refresh cycle.
 */
export const startProactiveRefresh = () => {
  scheduleProactiveRefresh();
};

/**
 * Clear tokens and reset state (for logout).
 */
export const clearTokens = () => {
  stopProactiveRefresh();
  isLoggingOut = false; // Reset so we can log in again
  consecutiveFailures = 0;
};

// Request interceptor - add owner scope to eligible endpoints
axiosInstance.interceptors.request.use(
  async (config) => {
    // If we're in the process of logging out, reject all requests
    if (isLoggingOut) {
      return Promise.reject(new Error("Logging out"));
    }

    // If we've had too many consecutive failures, add a delay
    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      await new Promise((resolve) => setTimeout(resolve, BACKOFF_DELAY));
    }

    // Add owner_scope to eligible endpoints. Team-level configuration surfaces
    // (e.g. the Team Goals modal) opt out with `skipOwnerScope: true`: the sidebar
    // scope is a viewing filter, not a write scope, so a team goal must be
    // previewed against team-wide data. (PAN-587)
    if (!config.skipOwnerScope && shouldAddOwnerScope(config.url)) {
      config.url = addOwnerScopeToUrl(config.url);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor - handle 401/403 with token refresh
axiosInstance.interceptors.response.use(
  (response) => {
    // Reset consecutive failures on successful response
    consecutiveFailures = 0;
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Track network errors
    if (isNetworkError(error)) {
      consecutiveFailures++;
      console.error(
        `Network error (attempt ${consecutiveFailures}):`,
        error.message,
      );

      if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        error.message =
          "Unable to connect to server. Please check your internet connection and try again later.";
      }
      return Promise.reject(error);
    }

    // Handle 401/403 errors - attempt token refresh
    // 401: Token expired or invalid
    // 403: Can occur when access token cookie is missing (browser deleted expired cookie)
    if (isAuthError(error.response?.status) && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // If we're already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return axiosInstance(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        await refreshToken();
        processQueue(null);
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export { isNetworkError };
